<template>
  <div data-testid="page-order-summary">
    <p class="txt-body-m-regular">{{ $t('FestivalShop.orders.summary.total') }}</p>
    <p class="txt-body-m-semibold">{{ formatPrice(total, locale) }}</p>
  </div>
  <div>
    <p class="txt-body-s text-subtlest">{{ $t('FestivalShop.orders.summary.event') }}</p>
    <p class="txt-body-m-regular">{{ order.rootEvent.name ?? order.kiosk.name }}</p>
  </div>
  <div>
    <p class="txt-body-s text-subtlest">{{ $t('FestivalShop.orders.summary.ordered_on') }}</p>
    <p>{{ formatDate(order.createdAt, true) }}</p>
  </div>
  <div>
    <p class="txt-body-s text-subtlest">{{ $t('FestivalShop.orders.summary.ordered_by') }}</p>
    <p class="txt-body-m-regular">{{ billingContact }}</p>
  </div>
  <div>
    <p class="txt-body-s text-subtlest">{{ $t('FestivalShop.orders.summary.ordered_items') }}</p>
    <p class="txt-body-m-regular">
      {{
        `${getOrderDisplayItems(order)
          .map((item) => item.count)
          .reduce((sum, count) => sum + count, 0)} ${$t('FestivalShop.orders.summary.ordered_items_items')}`
      }}
    </p>
  </div>
  <div>
    <h4 class="txt-headline-xs">{{ $t('FestivalShop.orders.summary.billing_address') }}</h4>
    <p class="mt-3 txt-body-m">
      {{ billingContact }} <br />
      {{ billingCompany }}<br />
      {{ billingStreet }}<br />
      {{ billingCity }}<br />
      {{ order.country }}<br />
    </p>
    <button class="btn-secondary-neutral-xs mt-3" :disabled="hasExpiredCategory" @click="showEditModal = true">
      {{ $t('FestivalShop.orders.summary.edit_address') }}
    </button>
  </div>
  <EditBillingAddressModal v-if="showEditModal" :order="order" :event-slug="eventSlug" :kiosk-id="kioskId" @close="showEditModal = false" />
</template>
<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'

import { formatDate, formatPrice } from '@/components/FestivalShop/helpers'
import { useLocaleStore } from '@/stores'

import { getOrderDisplayItems, type ExtrasOrderNode } from '..'
import EditBillingAddressModal from './EditBillingAddressModal.vue'

interface Props {
  order: ExtrasOrderNode
  eventSlug: string
  kioskId: string
}

const props = defineProps<Props>()

const showEditModal = ref(false)

const { locale } = storeToRefs(useLocaleStore())

const total = computed(() => {
  return getOrderDisplayItems(props.order)
    .map((item) => item.price * item.count + (item.deliveryFee ?? 0))
    .reduce((sum, x) => sum + x, 0)
})

const billingContact = computed(() => {
  return [props.order.title, props.order.firstName, props.order.lastName].filter(Boolean).join(' ')
})

const billingCompany = computed(() => {
  return props.order.company || props.order.partnerCompany.name
})

const billingStreet = computed(() => {
  return [props.order.street, props.order.number].filter(Boolean).join(' ')
})

const billingCity = computed(() => {
  return [props.order.zip, props.order.city].filter(Boolean).join(' ')
})

const hasExpiredCategory = computed(() => {
  const now = Date.now()
  return props.order.extras.some((extra) => {
    const expiresAt = extra.product.productCategory?.expiresAt
    return expiresAt ? new Date(expiresAt).getTime() <= now : false
  })
})
</script>
